<script setup lang="ts">
import { Levels } from '@/constant/enums';
import { computed, ref, toRefs } from 'vue';

const props = defineProps<{
  levels: string[];
  printSelectedLevel?: string;
}>();
const { levels, printSelectedLevel } = toRefs(props);
const activeLevel = ref('');

const emits = defineEmits(['selectedLevel']);

const currentActiveLevel = computed(() => {
  return printSelectedLevel.value
    ? printSelectedLevel.value
    : activeLevel.value;
});

const getLevelName = (level: string) => {
  return Levels[level];
};

const setActiveLevel = (level: string) => {
  activeLevel.value = activeLevel.value === level ? '' : level;
  emits('selectedLevel', activeLevel.value.trim());
};

defineExpose({
  setActiveLevel,
});
</script>

<template>
  <div class="townhome-level">
    <div class="townhome-level-left">
      <i>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="lucide lucide-layers-icon lucide-layers"
        >
          <path
            d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"
          />
          <path
            d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"
          />
          <path
            d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"
          />
        </svg>
      </i>
      LEVEL
    </div>
    <div class="townhome-level-right">
      <div
        v-for="(level, index) in levels"
        :key="index"
        class="townhome-level-item"
        :class="{ 'item-active': currentActiveLevel === level }"
        @click="setActiveLevel(level)"
      >
        {{ getLevelName(level) }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.townhome-level {
  position: absolute;
  right: 0;
  bottom: 30px;
  transition: all 0.6s linear;
  bottom: var(--bottomSpacelegend);
  left: var(--leftSidebarWidth);
  z-index: 9;
  border-radius: 100vmax;
  padding: 13px 35px;
  box-shadow: 0px 2px 3px 0px rgba(163, 131, 63, 0.13);
  overflow: hidden;
  max-width: max-content;
  background: #fff;
  margin-inline: auto;
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
  @include ipad-mini {
    padding: 12px 24px;
  }
  @include xl {
    justify-content: space-evenly;
  }

  &-left {
    display: flex;
    align-items: center;
    gap: 8px;
    font: 600 15px/1.2 'Open Sans';
    color: #353535;
  }
  &-right {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 1px;
    @include ipad-mini {
      gap: 8px;
    }
  }
  .townhome-level-item {
    padding: 6px 12px;
    font: 600 14px/1.2 'Open Sans';
    color: #353535;
    cursor: pointer;
    border-radius: 100vmax;
    position: relative;
    text-align: center;
    @include ipad-mini {
      padding: 6px 8px;
      font-size: 12px;
    }
    &:not(:last-child) {
      &::after {
        content: '';
        position: absolute;
        display: block;
        width: 1px;
        height: 100%;
        background: #e0e0e0;
        right: -8px;
        top: 0;
        @include ipad-mini {
          right: -4px;
        }
      }
    }

    &:first-child {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &.item-active {
      background: #e0e0e0;
    }
  }
}

.townhome-level.customer-mode {
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  width: 60px;
  right: 20px;
  left: auto;
  top: 225px;
  bottom: auto;
  padding: 0;
  display: block;
  .townhome-level-left {
    font-size: 14px;
    background: #656f74;
    color: #fff;
    padding: 6px;
    border-bottom: 1px solid #e0e0e0;
    text-align: center;
    display: block;
    i {
      display: none;
    }
  }
  .townhome-level-right {
    display: block;
    margin-top: 0;
  }
  .townhome-level-item {
    padding: 6px;
    border-radius: 0;
    display: block;
    border-bottom: 1px solid #e0e0e0;
    &::after {
      display: none;
    }
    &:last-child {
      border-bottom: none;
    }
  }
}
</style>
